import { Group, Indicator, LoadingOverlay, ScrollArea, Tabs } from '@mantine/core';
import { JSX, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { EnvironmentCountries, LowValuesDeclarantDataSource } from '@e-origin/shared';

import { ContentLayout } from '@features/layout';
import { useDeclarant } from '@hooks/declarant';
import { useUser } from '@hooks/user';
import { useSettings } from '@hooks/useSettings';
import { InvitationTokens } from '@utils';
import { useOverride } from '../../override';
import { B2CReports } from './B2CReports';
import { CustomsAuthenticationForm } from './CustomsAuthenticationForm';
import { CustomsRegister } from './CustomsRegister';
import { CustomsReports } from './CustomsReports';
import { DeclarantForm } from './DeclarantForm';
import { DefaultCustomersAndTemplatesForm } from './DefaultCustomersAndTemplatesForm';
import { GoodsLocation } from './GoodsLocation';
import { GuaranteeQuery } from './GuaranteeQuery';
import classes from './ManageAccount.module.css';
import { ManageUsers } from './ManageUsers';
import { PaymentsForm } from './PaymentsForm';
import { RepresentativeForm } from './RepresentativeForm';
import { RisksCalculationForm } from './RisksCalculationForm';
import { ValidationSettingsForm } from './ValidationSettingsForm';
import { VATReports } from './VATReports';

interface ManageAccountTab {
  value: string;
  label: string | JSX.Element;
  condition: boolean;
  component: React.ReactNode;
}

// eslint-disable-next-line max-statements
export const ManageAccount = () => {
  const { data: settings } = useSettings(false);
  const { data: user } = useUser();
  const { data: declarant } = useDeclarant();
  const navigate = useNavigate();
  const { tabId } = useParams();
  const Override = useOverride();

  const invitationPending = !(user?.declarant as any)?.company && !!InvitationTokens.getTokens().length;

  const basePath = '/manage-account';

  const tabs: ManageAccountTab[] = [
    {
      value: 'declarant',
      label: settings?.isForwarder ? 'Account info' : 'Declarant (LV)',
      condition:
        !(settings?.isForwarder && !user?.isSupervisor) &&
        declarant?.functionalFeatures?.lowValuesDeclarantDataSource !== LowValuesDeclarantDataSource.CUSTOMER,
      component: <DeclarantForm editDisabled={false} />,
    },
    {
      value: 'representative',
      label: 'Representative (LV)',
      condition: !settings?.isForwarder && declarant?.functionalFeatures?.lowValuesDeclarantDataSource !== LowValuesDeclarantDataSource.CUSTOMER,
      component: <RepresentativeForm editDisabled={false} />,
    },
    { value: 'customs', label: 'Customs authentication', condition: !settings?.isForwarder, component: <CustomsAuthenticationForm editDisabled={false} /> },
    { value: 'location', label: 'Location of goods', condition: !(settings?.isForwarder && !user?.isSupervisor), component: <GoodsLocation /> },
    { value: 'payments', label: 'Payments', condition: !settings?.isForwarder, component: <PaymentsForm editDisabled={false} /> },
    { value: 'risks', label: 'Risks calculation', condition: true, component: <RisksCalculationForm /> },
    { value: 'validation', label: 'Validation Settings', condition: true, component: <ValidationSettingsForm /> },
    { value: 'vat', label: 'VAT reports', condition: !settings?.isForwarder, component: <VATReports /> },
    { value: 'customs-reports', label: 'Customs reports', condition: true, component: <CustomsReports /> },
    { value: 'customs-register', label: 'Customs register', condition: !settings?.isForwarder, component: <CustomsRegister /> },
    { value: 'historical', label: 'Historical data', condition: !settings?.isForwarder, component: <B2CReports /> },
    { value: 'guarantee', label: 'Guarantee query', condition: settings?.country === EnvironmentCountries.NL, component: <GuaranteeQuery /> },
    { value: 'users', label: 'Users', condition: user?.isSupervisor, component: <ManageUsers /> },
    {
      value: 'default-customers-and-templates',
      label: 'Default customers/templates',
      condition: true,
      component: <DefaultCustomersAndTemplatesForm />,
    },
    {
      value: 'wallet',
      label: (
        <Group justify="space-between">
          <span>Wallet</span>
          {invitationPending && <Indicator processing color="red" mr="sm" />}
        </Group>
      ),
      condition: settings?.walletEnabled && (!settings?.isForwarder || user?.isSupervisor),
      component: <Override.WalletInformation />,
    },
  ];

  const availableTabs = tabs.filter((tab) => tab.condition);

  useEffect(() => {
    if (!tabId && availableTabs.length > 0) {
      navigate(`${basePath}/${availableTabs[0].value}`);
    }
  }, [tabId, availableTabs, navigate]);

  const handleTabChange = (value: string) => {
    navigate(`${basePath}/${value}`);
  };

  const currentTab = availableTabs.find((tab) => tab.value === tabId);

  if (!settings || !user) {
    return <LoadingOverlay visible overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  if (tabId && !currentTab && availableTabs.length > 0) {
    return <Navigate to={`${basePath}/${availableTabs[0].value}`} replace />;
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Account</ContentLayout.Title>
      </ContentLayout.Header>

      <ContentLayout.SubHeader>
        <Tabs value={tabId} onChange={handleTabChange} orientation="vertical" keepMounted={false}>
          <Tabs.List>
            {availableTabs.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {availableTabs.map((tab) => (
            <Tabs.Panel key={tab.value} value={tab.value} pl={30}>
              <ScrollArea classNames={classes} offsetScrollbars>
                {tab.component}
              </ScrollArea>
            </Tabs.Panel>
          ))}
        </Tabs>
      </ContentLayout.SubHeader>
    </ContentLayout>
  );
};
